import React, { useState } from "react";
import { IContactFromData } from "../../exports/interfaces";
import { useCompanyDetailsContext } from "../../context/CompanyDetailsContext";
import { formatUrl, getFormattedDate } from "../../exports/functions";
import DynamicToolTip from "../DynamicToolTip";
import { useLoader } from "../../context/LoaderContext";
import { dynamicFailed, dynamicSuccess } from "../../hooks/useToast";
import axios from "axios";
import { headers } from "../../exports/exports";
import { sendErrorLog } from "../ErrorLog";

const ContactForm = (props: { contactFormData: IContactFromData[] }) => {
  const { setLoading } = useLoader();
  const { companyDetailsData } = useCompanyDetailsContext();
  const [firstname, setFirstname] = useState<string>();
  const [lastname, setLastname] = useState<string>();
  const [phone, setPhone] = useState<string>();
  const [email, setEmail] = useState<string>();
  const [message, setMessage] = useState<string>();
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const isValidEmail = (email: string) => /\S+@\S+\.\S+/.test(email);
  const validateStep = () => {
    let newErrors: { [key: string]: string } = {};
    if (!email) {
      newErrors.email = "Email is required";
    } else if (!isValidEmail(email)) {
      newErrors.email = "Invalid email format";
    }
    if (!firstname) {
      newErrors.firstname = "First Name is required";
    }
    if (!lastname) {
      newErrors.lastname = "Last Name is required";
    }
    if (!phone) {
      newErrors.phone = "Phone Number is required";
    } else if (!/^\d{10}$/.test(phone)) {
      newErrors.phone = "Phone Number must be exactly 10 digits";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async () => {
    if (!validateStep()) {
      return;
    }
    setLoading(true);
    const data = {
      data: {
        First_Name: firstname,
        Last_Name: lastname,
        Email: email,
        Phone: phone,
        Message: message,
      },
    };
    axios
      .post(
        `${process.env.REACT_APP_strapiUrl}/api/frcsit-contact-messages`,
        data,
        {
          headers,
        },
      )
      .then(async () => {
        const adminUsers = [
          {
            sisID: 1233,
            email: "frcsitsecretary@gmail.com",
          },
          {
            sisID: 1234,
            email: "secretary@frcsit.com",
          },
        ];
        const currentYear = getFormattedDate(0, 0).slice(0, 4);
        await axios.post(
          `${process.env.REACT_APP_NOTIFICATION_URL}/notification`,
          {
            templateId: "e9fff5dd-3a41-44ba-8db1-9d4883bcf0de",
            senderId: 5171,
            payload: {
              First_Name: firstname,
              Last_Name: lastname,
              Email: email,
              Phone: phone,
              Message: message,
              CurrentYear: currentYear,
            },
            users: adminUsers,
          },
        );
        setLoading(false);
        dynamicSuccess("Form submitted successfully!");
        setFirstname("");
        setLastname("");
        setPhone("");
        setEmail("");
        setMessage("");
      })
      .catch(async (e: any) => {
        await sendErrorLog(
          "Contact Form Submission Error",
          `${firstname} ${lastname}`,
          email ?? "",
          phone ?? "",
          e?.config?.url,
          e.message,
        );
        dynamicFailed(`${e.message}`);
        setLoading(false);
      });
  };
  return (
    <section className="contact-page">
      <div className="container">
        <div className="row">
          <div className="col-xl-5 col-lg-5">
            <div className="contact-page__left">
              <div className="section-title text-left">
                <span className="custom-fontweight">
                  {props?.contactFormData[0]?.primary_title}
                </span>
                <h2 className="section-title__title">
                  {props?.contactFormData[0]?.secondary_title}
                </h2>
              </div>
              <p className="contact-page__text">
                {props?.contactFormData[0]?.description}
              </p>
              <div className="contact-page__social">
                {companyDetailsData[0]?.twitterx_link !== null && (
                  <DynamicToolTip title="TwitterX">
                    <a
                      href={formatUrl(companyDetailsData[0]?.twitterx_link)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        cursor: "pointer",
                      }}
                    >
                      <i className="fab fa-twitter"></i>
                    </a>
                  </DynamicToolTip>
                )}
                {companyDetailsData[0]?.facebook_link !== null && (
                  <DynamicToolTip title="Facebook">
                    <a
                      href={formatUrl(companyDetailsData[0]?.facebook_link)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        cursor: "pointer",
                      }}
                    >
                      <i className="fab fa-facebook"></i>
                    </a>
                  </DynamicToolTip>
                )}
                {companyDetailsData[0]?.instagram_link !== null && (
                  <DynamicToolTip title="Instagram">
                    <a
                      href={formatUrl(companyDetailsData[0]?.instagram_link)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        cursor: "pointer",
                      }}
                    >
                      <i className="fab fa-instagram"></i>
                    </a>
                  </DynamicToolTip>
                )}
                {companyDetailsData[0]?.linkedin_link !== null && (
                  <DynamicToolTip title="LinkedIn">
                    <a
                      href={formatUrl(companyDetailsData[0]?.linkedin_link)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        cursor: "pointer",
                      }}
                    >
                      <i className="icon-linkedin"></i>
                    </a>
                  </DynamicToolTip>
                )}
                {companyDetailsData[0]?.tiktok_link !== null && (
                  <DynamicToolTip title="Tiktok">
                    <a
                      href={formatUrl(companyDetailsData[0]?.tiktok_link)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        cursor: "pointer",
                      }}
                    >
                      <i className="icon-tiktok"></i>
                    </a>
                  </DynamicToolTip>
                )}
              </div>
            </div>
          </div>
          <div className="col-xl-7 col-lg-7">
            <div className="contact-page__right">
              <div className="comment-one__form contact-form-validated">
                <div className="row">
                  <div className="col-xl-6">
                    <div className="comment-form__input-box">
                      <input
                        type="text"
                        placeholder="First Name*"
                        name="firstname"
                        value={firstname}
                        onChange={(e) => setFirstname(e.target.value)}
                        className={errors.firstname && "error-border"}
                      />
                      {errors.firstname && (
                        <p className="error-message">{errors.firstname}</p>
                      )}
                    </div>
                  </div>
                  <div className="col-xl-6">
                    <div className="comment-form__input-box">
                      <input
                        type="text"
                        placeholder="Last Name*"
                        name="lastname"
                        value={lastname}
                        onChange={(e) => setLastname(e.target.value)}
                        className={errors.lastname && "error-border"}
                      />
                      {errors.lastname && (
                        <p className="error-message">{errors.lastname}</p>
                      )}
                    </div>
                  </div>
                  <div className="col-xl-6">
                    <div className="comment-form__input-box">
                      <input
                        type="email"
                        placeholder="Email Address*"
                        name="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className={errors.email && "error-border"}
                      />
                      {errors.email && (
                        <p className="error-message">{errors.email}</p>
                      )}
                    </div>
                  </div>
                  <div className="col-xl-6">
                    <div className="comment-form__input-box">
                      <input
                        type="number"
                        placeholder="Phone Number*"
                        name="phone"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        onWheel={(e) => e.currentTarget.blur()}
                        className={`${
                          errors.phone ? "error-border" : ""
                        } no-spinner`}
                      />
                      {errors.phone && (
                        <p className="error-message">{errors.phone}</p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-xl-12">
                    <div className="comment-form__input-box text-message-box">
                      <textarea
                        name="message"
                        placeholder="Message*"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                      ></textarea>
                    </div>
                    <div className="comment-form__btn-box">
                      <button
                        className="thm-btn comment-form__btn"
                        onClick={handleSubmit}
                      >
                        Submit
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
